import React, { useEffect, useRef } from 'react';

const ParticleBackground = ({ className }) => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];
    
    // Set canvas dimensions to match parent
    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      canvas.width = parent.offsetWidth;
      canvas.height = parent.offsetHeight;
      
      // Re-initialize particles when canvas size changes
      initParticles();
    };
    
    // Initialize particles
    const initParticles = () => {
      particles = [];
      // Reduced particle count for better performance
      const particleCount = Math.min(Math.floor(canvas.width * canvas.height / 20000), 50);

      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 1.5 + 0.5, // Smaller particles
          speedX: (Math.random() - 0.5) * 0.3, // Slower movement
          speedY: (Math.random() - 0.5) * 0.3,
          // For wave effect
          initialY: Math.random() * canvas.height,
          waveAmplitude: Math.random() * 15 + 3, // Reduced amplitude
          waveFrequency: Math.random() * 0.015 + 0.005, // Slower frequency
          waveOffset: Math.random() * Math.PI * 2
        });
      }
    };
    
    // Draw particles and connections
    const drawParticles = (time) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw particles
      particles.forEach((particle, i) => {
        // Move particle with a wave-like motion
        particle.x += particle.speedX;
        
        // Wave motion - quantum rise and fall effect
        particle.y = particle.initialY + Math.sin(time * particle.waveFrequency + particle.waveOffset) * particle.waveAmplitude;
        
        // Wrap around screen
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;
        
        // Draw the particle
        ctx.fillStyle = 'rgba(236, 72, 153, 0.5)'; // Pink color from our theme
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
        
        // Connect particles that are close to each other (optimized with distance check)
        // Only check a subset of particles for connections to reduce complexity
        const maxConnections = 5; // Limit connections per particle
        let connections = 0;

        for (let j = i + 1; j < particles.length && connections < maxConnections; j++) {
          const dx = particles[j].x - particle.x;
          const dy = particles[j].y - particle.y;
          const distanceSquared = dx * dx + dy * dy; // Use squared distance to avoid sqrt

          if (distanceSquared < 10000) { // 100^2 = 10000
            const distance = Math.sqrt(distanceSquared);
            ctx.beginPath();
            ctx.strokeStyle = `rgba(236, 72, 153, ${0.15 * (1 - distance / 100)})`; // Slightly more transparent
            ctx.lineWidth = 0.3; // Thinner lines
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
            connections++;
          }
        }
      });
    };
    
    // Animation loop
    const animate = (time) => {
      drawParticles(time * 0.001); // Convert to seconds
      animationFrameId = requestAnimationFrame(animate);
    };
    
    // Handle window resize
    window.addEventListener('resize', resizeCanvas);
    
    // Initialize canvas and start animation
    resizeCanvas();
    animate(0);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  
  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 z-0 ${className}`}
      style={{ pointerEvents: 'none' }}
    />
  );
};

export default ParticleBackground;